/**
 * Vehicle Search results page — filters server-rendered product cards
 * by bolt-index vehicle SKUs (+ optional universal-fit tag), then appends
 * unmatched accessories (products also in the `accessories` collection).
 */
(function () {
  const skuBase = (raw) => {
    const s = String(raw || '')
      .trim()
      .toLowerCase();
    const match = s.match(/^(.*?)-r\d+$/i);
    return match ? match[1] : s;
  };

  const parseSkuList = (raw) => {
    return String(raw || '')
      .split(',')
      .map((s) => s.trim().toLowerCase())
      .filter(Boolean);
  };

  const buildVehicleSkuSet = (vehicle) => {
    const set = new Set();
    if (!vehicle) return set;
    let skus = vehicle.skus;
    if (typeof skus === 'string') skus = skus.split(',');
    if (!Array.isArray(skus)) return set;
    skus.forEach((raw) => {
      const sku = String(raw || '')
        .trim()
        .toLowerCase();
      if (!sku) return;
      set.add(sku);
      set.add(skuBase(sku));
    });
    return set;
  };

  const cardMatchesVehicle = (card, vehicleSkuSet, universalFitTag) => {
    if (universalFitTag) {
      const tags = String(card.getAttribute('data-tags') || '')
        .toLowerCase()
        .split(',')
        .map((t) => t.trim());
      if (tags.includes(universalFitTag)) return true;
    }
    if (!vehicleSkuSet || vehicleSkuSet.size === 0) return false;
    const cardSkus = parseSkuList(card.getAttribute('data-skus'));
    return cardSkus.some((sku) => vehicleSkuSet.has(sku) || vehicleSkuSet.has(skuBase(sku)));
  };

  const getParams = () => {
    const params = new URLSearchParams(window.location.search);
    return {
      make: (params.get('make') || '').trim(),
      model: (params.get('model') || '').trim(),
      year: (params.get('year') || '').trim(),
      vid: (params.get('vid') || '').trim(),
    };
  };

  const findByLabel = (list, label, labelKey) => {
    const wanted = String(label || '')
      .trim()
      .toLowerCase();
    if (!wanted || !Array.isArray(list)) return null;
    return (
      list.find((item) => {
        const value = String(item[labelKey] != null ? item[labelKey] : item.label || '')
          .trim()
          .toLowerCase();
        return value === wanted;
      }) || null
    );
  };

  const resolveVehicleId = async ({ make, model, year, vid }) => {
    if (vid) return vid;
    if (!make || !model || !year) return null;
    if (
      typeof window.get_vehicle_makes !== 'function' ||
      typeof window.get_vehicle_models !== 'function' ||
      typeof window.get_vehicle_years !== 'function'
    ) {
      return null;
    }

    const makes = await window.get_vehicle_makes();
    const makeMatch = findByLabel(makes, make, 'make');
    if (!makeMatch || !makeMatch.id) return null;

    const models = await window.get_vehicle_models(makeMatch.id);
    const modelMatch = findByLabel(models, model, 'model');
    if (!modelMatch || !modelMatch.id) return null;

    const years = await window.get_vehicle_years(modelMatch.id);
    const yearMatch = findByLabel(years, year, 'year');
    if (!yearMatch || !yearMatch.id) return null;
    return String(yearMatch.id);
  };

  const writeHeading = (root, { make, model, year }, template) => {
    const heading = root.querySelector('[data-vehicle-results-heading]');
    if (!heading) return;
    const tpl = template || 'Gear for __MAKE__, __MODEL__, __YEAR__';
    if (make && model && year) {
      heading.textContent = tpl
        .replace(/__MAKE__/g, make)
        .replace(/__MODEL__/g, model)
        .replace(/__YEAR__/g, year);
    } else if (make || model || year) {
      heading.textContent = ['Gear for', [make, model, year].filter(Boolean).join(', ')].join(' ');
    }
  };

  const showEmpty = (root, visible, text) => {
    const empty = root.querySelector('[data-vehicle-results-empty]');
    const emptyText = root.querySelector('[data-vehicle-results-empty-text]');
    if (!empty) return;
    if (text && emptyText) emptyText.textContent = text;
    empty.hidden = !visible;
    empty.classList.toggle('is-visible', visible);
  };

  const compactAlphaNum = (value) =>
    String(value || '')
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, '');

  const cardTextBlob = (card) => {
    const title = card.getAttribute('data-title') || '';
    const handle = card.getAttribute('data-handle') || '';
    const combined = `${title} ${handle}`;
    return {
      raw: combined.toLowerCase(),
      compact: compactAlphaNum(combined),
    };
  };

  /**
   * Expand API labels like "4runner (3rd Row Seats)" into match candidates:
   * full label, label without parentheticals, and primary token before " - "/" / ".
   */
  const vehicleTokenCandidates = (token) => {
    const value = String(token || '').trim();
    if (!value) return [];
    const candidates = [value];
    const withoutParens = value.replace(/\([^)]*\)/g, ' ').replace(/\s+/g, ' ').trim();
    if (withoutParens && withoutParens !== value) candidates.push(withoutParens);
    const primary = withoutParens.split(/\s+[\/|–—-]\s+/)[0].trim();
    if (primary && !candidates.includes(primary)) candidates.push(primary);
    return candidates.filter((c) => c.length >= 2);
  };

  const textContainsVehicleToken = (blob, token) => {
    const candidates = vehicleTokenCandidates(token);
    for (const candidate of candidates) {
      const lower = candidate.toLowerCase();
      if (blob.raw.includes(lower)) return true;
      const compact = compactAlphaNum(candidate);
      if (compact.length >= 2 && blob.compact.includes(compact)) return true;
    }
    return false;
  };

  /** 2 = model in title/handle, 1 = make only, 0 = no name match */
  const scoreMatchedCard = (card, make, model) => {
    const blob = cardTextBlob(card);
    if (model && textContainsVehicleToken(blob, model)) return 2;
    if (make && textContainsVehicleToken(blob, make)) return 1;
    return 0;
  };

  /**
   * Show MMY / universal-fit matches first (model-name boost, then make),
   * then append accessories that did not already match (unchanged order).
   */
  const filterGrid = (root, vehicleSkuSet, universalFitTag, options) => {
    const opts = options || {};
    const includeAccessories = opts.includeAccessories !== false;
    const make = opts.make || '';
    const model = opts.model || '';
    const grid = root.querySelector('[data-vehicle-results-grid]');
    const cards = Array.from(root.querySelectorAll('[data-vehicle-card]'));
    if (!grid) return 0;

    const matched = [];
    const accessoryOnly = [];

    cards.forEach((card, index) => {
      const isMatch = cardMatchesVehicle(card, vehicleSkuSet, universalFitTag);
      const isAccessory = card.getAttribute('data-accessory') === 'true';

      if (isMatch) {
        matched.push({ card, index, score: scoreMatchedCard(card, make, model) });
        return;
      }
      if (includeAccessories && isAccessory) {
        accessoryOnly.push(card);
        return;
      }
      card.hidden = true;
    });

    matched.sort((a, b) => {
      if (b.score !== a.score) return b.score - a.score;
      return a.index - b.index;
    });

    matched.forEach(({ card }) => {
      card.hidden = false;
      grid.appendChild(card);
    });
    accessoryOnly.forEach((card) => {
      card.hidden = false;
      grid.appendChild(card);
    });

    return matched.length + accessoryOnly.length;
  };

  const revealGrid = (root) => {
    const grid = root.querySelector('[data-vehicle-results-grid]');
    if (grid) grid.setAttribute('data-vehicle-results-pending', 'false');
  };

  const run = async () => {
    const root = document.querySelector('[data-vehicle-search-results]');
    if (!root) return;

    const cfg = window.__lunoVehicleResults || {};
    const params = getParams();
    writeHeading(root, params, cfg.headingTemplate);

    if (!params.make && !params.model && !params.year && !params.vid) {
      // No vehicle context — show nothing matched / prompt re-search
      filterGrid(root, new Set(), cfg.universalFitTag, { includeAccessories: false });
      showEmpty(root, true, cfg.emptyText);
      revealGrid(root);
      return;
    }

    try {
      const vehicleId = await resolveVehicleId(params);
      if (!vehicleId || typeof window.get_vehicle_by_id !== 'function') {
        filterGrid(root, new Set(), cfg.universalFitTag, { includeAccessories: false });
        showEmpty(root, true, cfg.emptyText);
        revealGrid(root);
        return;
      }

      const vehicle = await window.get_vehicle_by_id(vehicleId);
      const vehicleSkuSet = buildVehicleSkuSet(vehicle);

      const visibleCount = filterGrid(root, vehicleSkuSet, (cfg.universalFitTag || '').toLowerCase(), {
        make: params.make,
        model: params.model,
      });
      showEmpty(root, visibleCount === 0, cfg.emptyText);
      revealGrid(root);
    } catch (err) {
      console.error('[vehicle-search-results]', err);
      showEmpty(root, true, cfg.emptyText);
      revealGrid(root);
    }
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', run);
  } else {
    run();
  }
})();
